import _ from 'lodash';
import { CSVParse, CSVTransform } from '../lib/CsvAsync';

function hasAllEmptyProperties(obj) {
  for (var k in obj) {
    if (obj.hasOwnProperty(k)) {
      if (!_.isEmpty(obj[k])) {
        return false;
      }
    }
  }
  return true;
}

export const getDataMapColumns = (dataMap = {}) => {
  if (_.has(dataMap, 'map_entry_schema.properties')) {
    return dataMap.map_entry_schema.properties;
  } else {
    return {};
  }
};

export const getPropertiesFromCSV = (csv, delimiter = ',', map_primary_key = null) => {
  const lineBreakIndex = csv.indexOf('\n');
  const firstLine = lineBreakIndex < 0 ? csv : csv.slice(0, lineBreakIndex);
  const properties = _.compact(_.map(firstLine.split(delimiter), (value) => value.trim()));
  const primaryKey =
    map_primary_key && properties.indexOf(map_primary_key) > -1 ? map_primary_key : properties[0];
  const propertiesObj = {};
  properties.forEach((name, idx) => {
    propertiesObj[name] = {
      name,
      default: '',
      id: idx,
      type: 'string',
      is_prime_key: name === primaryKey,
    };
  });

  return { properties, propertiesObj, primaryKey };
};

export const getDataMapFromCSV = (csv, delimiter = ',') => {
  return CSVParse(csv, { delimiter }).then((rows) => {
    return CSVTransform(rows, (row) =>
      row === rows[0] ? null : _.mapKeys(row, (value, index) => rows[0][index]),
    );
  });
};

export const dataMapFields = [
  'id',
  'name',
  'description',
  'data_sink_id',
  'data_set_id',
  'map_primary_key',
  'data_defaults',
  'data_map',
];

export const dataMapMethods = {
  getFlatSchemaForDataMap(data) {
    let _data = {
      ...data,
      properties: [],
      keys: [],
      keysFiltered: [],
      keysPageData: [],
      keysPage: 1,
      keysTotalPage: 1,
      pageSize: 8,
      keysSearch: '',
      showDuplicates: false,
      bulkText: '',
      isSaving: false,
      isMutated: false,
    };
    dataMapMethods.generateProperties(_data);
    dataMapMethods.generateKeys(_data); // iterates over DM properties
    dataMapMethods.filterPaginateKeys(_data);
    dataMapMethods.generateBulkText(_data);
    return _data;
  },

  generateProperties(_this) {
    if (_this.map_entry_schema && _this.map_entry_schema.properties) {
      Object.keys(_this.map_entry_schema.properties).forEach((k) => {
        _this.properties.push({
          name: k,
          type: (_this.map_entry_schema.properties[k] || {}).type || 'string',
          default: _this.data_defaults[k],
          id: _.uniqueId(), // in case duplicate naming without breaking
        });
        if (k === _this.map_primary_key) {
          _this.map_primary_key_id = _this.properties[_this.properties.length - 1].id;
        }
      });
    }
  },

  generateKeys(_this) {
    (_this.data_map || []).forEach((k) => {
      let o = {};
      _this.properties.forEach((p) => {
        o[p.id] = k[p.name];
      });
      _this.keys.push(o);
    });
    _this.keys.push({});
  },

  filterPaginateKeys(_this) {
    _this.keysFiltered = [];
    if (_this.showDuplicates) {
      _this.keys.forEach((k, ix) => {
        if (k.warning) {
          _this.keysFiltered.push({ ...k, index: ix });
        }
      });
    } else if (_this.keysSearch) {
      _this.keys.forEach((k, ix) => {
        if (k.key.indexOf(_this.keysSearch) > -1) {
          _this.keysFiltered.push({ ...k, index: ix });
        }
      });
    } else {
      _this.keysFiltered = _this.keys;
    }
    _this.keysTotalPage = Math.ceil(_this.keysFiltered.length / _this.pageSize);
    _this.keysPageData = _this.keysFiltered.slice(
      (_this.keysPage - 1) * _this.pageSize,
      _this.keysPage * _this.pageSize,
    );
  },

  getMainProperties(_this) {
    let props = _.clone(_this.properties).sort(
      (p1, p2) => (p1.id === _this.map_primary_key_id) < (p2.id === _this.map_primary_key_id),
    );
    return {
      primary: props.shift() || {},
      dependant: props.shift() || {},
    };
  },

  generateBulkText(_this) {
    let { primary, dependant } = dataMapMethods.getMainProperties(_this);
    _this.bulkText = _this.keys
      .filter((k) => !hasAllEmptyProperties(k))
      .map((k) => k[primary.id] + ':' + k[dependant.id])
      .join('\n');
  },

  checkDuplicates(_this) {
    let { primary } = dataMapMethods.getMainProperties(_this);
    let keysIds = {};
    _this.isDuplicatedKey = false;
    _this.isMissingKey = false;
    _this.data_map = [];
    _this.keys.forEach((k) => {
      if (keysIds[k[primary.id]]) {
        if (k[primary.id]) {
          keysIds[k[primary.id]]++;
        }
      } else {
        keysIds[k[primary.id]] = 1;
      }
    });
    _this.keys.forEach((k, ix) => {
      k.duplicatedKey = false;
      if (keysIds[k[primary.id]] > 1) {
        k.duplicatedKey = true;
        _this.isDuplicatedKey = _this.isDuplicatedKey || k.duplicatedKey;
      }
      k.warning = k.duplicatedKey;
      if (!k.warning && k[primary.id]) {
        _this.data_map[ix] = {};
        _this.properties.forEach((p) => {
          _this.data_map[ix][p.name] = k[p.id];
        });
      }
    });

    dataMapMethods.filterPaginateKeys(_this);
  },

  setKeysState(_this) {
    dataMapMethods.checkDuplicates(_this);
    if (_this.properties.length === 2) {
      dataMapMethods.generateBulkText(_this);
    }
  },

  setPropertiesState(_this) {
    _this.data_defaults = {};
    _this.map_entry_schema = { properties: {} };
    _this.properties.forEach((p) => {
      _this.map_entry_schema.properties[p.name] = {};
      _this.map_entry_schema.properties[p.name].type = p.type;
      _this.data_defaults[p.name] = p.default;
      if (p.id === _this.map_primary_key_id) {
        _this.map_primary_key = p.name;
      }
    });
  },

  //public methods
  changeProperty(_this, key, value) {
    if (key === 'map_primary_key') {
      _this[key] = value.name;
      _this[key + '_id'] = value.id;
    } else {
      _this[key] = value;
    }
    _this.isMissingName = !_this.name;
  },

  updateProperties(_this, ix, key, value) {
    _this.properties[ix][key] = value;
    dataMapMethods.setPropertiesState(_this);
    if (key === 'name') {
      dataMapMethods.setKeysState(_this);
    }
  },

  addProperty(_this) {
    _this.properties[_this.properties.length] = {
      name: '',
      type: 'string',
      default: 'unknown',
      id: _.uniqueId(),
    };
    dataMapMethods.setPropertiesState(_this);
    dataMapMethods.setKeysState(_this);
  },

  removeProperty(_this, ix) {
    _this.properties.splice(ix, 1);
    dataMapMethods.setPropertiesState(_this);
    dataMapMethods.setKeysState(_this);
  },

  removeEmptyDataMaps(_this, ix) {
    for (var k = _this.keys.length - 1; k > -1; k--) {
      if (k !== ix && hasAllEmptyProperties(_this.keys[k])) {
        _this.keys.splice(k, 1);
      }
    }
  },

  addDataMap(_this, ix = 0) {
    if (ix === 0 && !hasAllEmptyProperties(_this.keys[ix])) {
      _this.keys.splice(ix, 0, {});
    } else if (
      ix > 0 &&
      !hasAllEmptyProperties(_this.keys[ix]) &&
      !hasAllEmptyProperties(_this.keys[ix - 1])
    ) {
      _this.keys.splice(ix, 0, {});
    }
    dataMapMethods.setKeysState(_this);
  },

  changeDataMap(_this, ix, type, value) {
    _this.keys[ix][type] = value;
    let allEmpty = hasAllEmptyProperties(_this.keys[ix]);
    if (ix === _this.keys.length - 1 && !allEmpty) {
      _this.keys.splice(_this.keys.length, 0, {});
    }
    if (allEmpty) {
      dataMapMethods.removeEmptyDataMaps(_this, ix);
    }
    dataMapMethods.setKeysState(_this);
  },

  removeDataMap(_this, ix) {
    if (_this.keys.length > 1) {
      _this.keys.splice(ix, 1);
    } else if (_this.keys.length === 1) {
      if (_this.keys[0].key || _this.keys[0].value) {
        _this.keys[0] = {};
      }
    }
    dataMapMethods.setKeysState(_this);
  },

  changeBulkText(_this, text) {
    if (_this.bulkText !== text) {
      _this.bulkText = text;
    }
  },

  updateDataMapFromBulkInsert(_this) {
    let { primary, dependant } = dataMapMethods.getMainProperties(_this);
    if (_this.oldBulkText !== _this.bulkText) {
      let keysStrings = _this.bulkText.split('\n');
      _this.keys = keysStrings.map((k) => {
        let key = k.split(':');
        return {
          [primary.id]: key[0],
          [dependant.id]: key[1],
        };
      });
      _this.oldBulkText = _this.bulkText;
      dataMapMethods.setKeysState(_this);
    }
    _this.tabSelected = 'keys';
    _this.updatingKeys = false;
  },

  goToFirstPage(_this) {
    _this.keysPage = 1;
    dataMapMethods.filterPaginateKeys(_this);
  },

  goToPrevPage(_this) {
    if (_this.keysPage > 1) {
      _this.keysPage--;
      dataMapMethods.filterPaginateKeys(_this);
    }
  },

  setPage(_this, page) {
    if (page < 1) {
      _this.keysPage = 1;
    } else if (page > _this.keysTotalPage) {
      _this.keysPage = _this.keysTotalPage;
    } else {
      _this.keysPage = page;
    }
    dataMapMethods.filterPaginateKeys(_this);
  },

  goToNextPage(_this) {
    if (_this.keysPage < _this.keysTotalPage) {
      _this.keysPage++;
      dataMapMethods.filterPaginateKeys(_this);
    }
  },

  goToLastPage(_this) {
    _this.keysPage = _this.keysTotalPage;
    dataMapMethods.filterPaginateKeys(_this);
  },

  setSearchTerm(_this, text) {
    _this.keysSearch = text;
    _this.keysPage = 1;
    dataMapMethods.filterPaginateKeys(_this);
  },

  setOnlyDuplicates(_this, showDuplicates) {
    _this.showDuplicates = showDuplicates;
    _this.keysPage = 1;
    dataMapMethods.filterPaginateKeys(_this);
  },
};
